import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { workoutShareService, SharedWorkoutResponse } from '../services/workoutShareService';
import { Workout, MemberExercise, WorkoutSet } from '../types/Workout';
import logo from '../assets/logo.jpeg';

interface SharedWorkoutData {
  memberName: string;
  workouts: Workout[];
}

const WorkoutPlanShared: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [workoutData, setWorkoutData] = useState<SharedWorkoutData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeWorkoutIndex, setActiveWorkoutIndex] = useState(0);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{url: string, alt: string} | null>(null);
  const fetchedRef = useRef(false);
  const workoutRefs = useRef<(HTMLDivElement | null)[]>([]);
  const navContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (token && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchSharedWorkout();
    }
  }, [token]);

  const fetchSharedWorkout = async () => {
    try {
      setLoading(true);
      const response = await workoutShareService.getSharedWorkout(token!);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to load workout plan');
      }
      const data: SharedWorkoutResponse = response.data;
      // Handle both 'workouts' and 'exercises' response formats for backward compatibility
      const workouts = (data as any).workouts || (data as any).exercises || [];
      const sortedWorkouts = workouts.sort((a: Workout, b: Workout) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      setWorkoutData({ memberName: data.memberName, workouts: sortedWorkouts });
      // Initialize workout refs array
      workoutRefs.current = new Array(sortedWorkouts.length).fill(null);
      // Update scroll indicators after data loads
      setTimeout(updateScrollIndicators, 300);
    } catch (err: any) {
      console.error('Error fetching shared workout:', err);
      setError(err.message || 'Failed to load workout plan');
    } finally {
      setLoading(false);
    }
  };

  // Check scroll indicators for navigation
  const updateScrollIndicators = () => {
    const container = navContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const hasOverflow = scrollWidth > clientWidth;
    
    setShowLeftGradient(scrollLeft > 5);
    setShowRightGradient(hasOverflow && scrollLeft < scrollWidth - clientWidth - 5);
  };

  // Scroll navigation left/right
  const scrollNavLeft = () => {
    const container = navContainerRef.current;
    if (!container) return;
    container.scrollBy({ left: -100, behavior: 'smooth' });
  };

  const scrollNavRight = () => {
    const container = navContainerRef.current;
    if (!container) return;
    container.scrollBy({ left: 100, behavior: 'smooth' });
  };

  // Handle image modal
  const openImageModal = (imageUrl: string, altText: string) => {
    setSelectedImage({ url: imageUrl, alt: altText });
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && selectedImage) {
        closeImageModal();
      }
    };

    if (selectedImage) {
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [selectedImage]);

  // Scroll navigation to show active item
  const scrollNavToActiveItem = (index: number) => {
    const container = navContainerRef.current;
    if (!container) return;

    const navItems = container.children;
    const activeItem = navItems[index] as HTMLElement;
    if (!activeItem) return;

    const containerRect = container.getBoundingClientRect();
    const itemRect = activeItem.getBoundingClientRect();
    
    const containerLeft = containerRect.left;
    const containerRight = containerRect.right;
    const itemLeft = itemRect.left;
    const itemRight = itemRect.right;

    if (itemLeft < containerLeft || itemRight > containerRight) {
      const scrollLeft = activeItem.offsetLeft - (container.clientWidth / 2) + (activeItem.clientWidth / 2);
      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // Scroll to specific workout
  const scrollToWorkout = (index: number) => {
    const workoutElement = workoutRefs.current[index];
    if (workoutElement) {
      const offsetTop = workoutElement.offsetTop - 80; // Account for header height
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
      setActiveWorkoutIndex(index);
      scrollNavToActiveItem(index);
    }
  };

  // Set up intersection observer to track visible workout
  useEffect(() => {
    if (!workoutData || workoutData.workouts.length === 0) return;

    const options = {
      root: null,
      rootMargin: '-80px 0px -50% 0px', // Account for header and center detection
      threshold: 0.3
    };

    const observer = new IntersectionObserver((entries) => {
              entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = workoutRefs.current.findIndex(ref => ref === entry.target);
            if (index !== -1) {
              setActiveWorkoutIndex(index);
              setTimeout(() => scrollNavToActiveItem(index), 100);
            }
          }
        });
    }, options);

    workoutRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      workoutRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [workoutData]);

  // Set up navigation scroll indicators
  useEffect(() => {
    const container = navContainerRef.current;
    if (!container) return;

    const handleScroll = () => updateScrollIndicators();
    const handleResize = () => updateScrollIndicators();

    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    // Set up ResizeObserver to detect when container size changes
    const resizeObserver = new ResizeObserver(() => {
      updateScrollIndicators();
    });
    resizeObserver.observe(container);
    
    // Initial check with multiple delays to ensure proper rendering
    setTimeout(updateScrollIndicators, 100);
    setTimeout(updateScrollIndicators, 300);
    setTimeout(updateScrollIndicators, 600);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
    };
  }, [workoutData]);

  if (loading) {
    return (
      <div className="shared-workout-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading workout plan...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="shared-workout-container">
        <div className="error-state">
          <div className="error-icon">
            <i className="ri-error-warning-line"></i>
          </div>
          <h3>Unable to load workout plan</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!workoutData) {
    return null;
  }

  return (
    <div className="shared-workout-container">
      <div className="shared-header">
        <div className="header-content">
          <img 
            src={logo} 
            alt="Cenadz Fitness Logo" 
            className="cenadz-logo"
          />
          <span className="header-text">Workout Plan - {workoutData.memberName}</span>
        </div>
      </div>

      <div className="shared-content">
        {workoutData.workouts.length === 0 ? (
          <div className="empty-workout">
            <p>No workouts in this plan</p>
          </div>
        ) : (
          <div className="workouts-shared-list">
            {workoutData.workouts.map((workout: Workout, workoutIndex: number) => (
              <div 
                key={workout.id} 
                className="shared-workout-card"
                ref={(el) => { workoutRefs.current[workoutIndex] = el; }}
              >
                <div className="shared-workout-header">
                  <h2>{workout.workoutName}</h2>
                  <div className="workout-meta">
                    <span className="exercise-count">{workout.exercises?.length || 0} exercises</span>
                    <span className="workout-date">Created: {new Date(workout.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
                
                {workout.exercises && workout.exercises.length > 0 && (
                  <div className="exercises-shared-list">
                    {workout.exercises.map((exercise: MemberExercise, exerciseIndex: number) => (
                      <div key={exercise.id} className="shared-exercise-card">
                                                 <div className="shared-exercise-header">
                           <div className="exercise-info">
                             {exercise.exerciseImageUrl && (
                               <img 
                                 src={exercise.exerciseImageUrl} 
                                 alt={exercise.exerciseName}
                                 className="exercise-image-shared clickable"
                                 onClick={() => openImageModal(exercise.exerciseImageUrl!, exercise.exerciseName)}
                                 onError={(e) => {
                                   e.currentTarget.style.display = 'none';
                                 }}
                               />
                             )}
                             <div className="shared-exercise-text">
                               <h3>{exercise.exerciseName}</h3>
                               {exercise.exerciseMuscleGroups && (
                                 <span className="shared-muscle-groups">{exercise.exerciseMuscleGroups}</span>
                               )}
                               {exercise.exerciseDescription && (
                                 <p className="shared-exercise-description">{exercise.exerciseDescription}</p>
                               )}
                             </div>
                           </div>
                         </div>
                        
                        <div className="sets-shared-list">
                          <div className="sets-header">
                            <span>Set</span>
                            <span>Weight</span>
                            <span>Reps</span>
                            <span>Type</span>
                          </div>
                          {exercise.sets && exercise.sets.map((set: WorkoutSet, setIndex: number) => (
                            <div key={setIndex} className="shared-set-row">
                              <div className="set-number">{set.setNumber}</div>
                              <div className="set-weight">{set.weight} kg</div>
                              <div className="set-reps">{set.reps}</div>
                              <div className={`set-type ${set.isWarmup ? 'warmup' : 'working'}`}>
                                {set.isWarmup ? 'Warmup' : 'Working'}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Floating Navigation Bar */}
      {workoutData && workoutData.workouts.length > 1 && (
        <div className="floating-workout-nav">
          {/* Left Arrow */}
          {showLeftGradient && (
            <button 
              className="nav-arrow nav-arrow-left"
              onClick={scrollNavLeft}
              aria-label="Scroll left"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}

          <div className="workout-nav-container" ref={navContainerRef}>
            {workoutData.workouts.map((workout: Workout, index: number) => (
              <button
                key={workout.id}
                className={`workout-nav-item ${index === activeWorkoutIndex ? 'active' : ''}`}
                onClick={() => scrollToWorkout(index)}
                title={workout.workoutName}
              >
                <span className="workout-nav-text">
                  {workout.workoutName}
                </span>
              </button>
            ))}
          </div>

          {/* Right Arrow */}
          {showRightGradient && (
            <button 
              className="nav-arrow nav-arrow-right"
              onClick={scrollNavRight}
              aria-label="Scroll right"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-container" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={closeImageModal}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <img 
              src={selectedImage.url} 
              alt={selectedImage.alt}
              className="image-modal-content"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <div className="image-modal-caption">
              {selectedImage.alt}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default WorkoutPlanShared; 
